import AppLoader from 'components/AppLoader';
import type { History } from 'history';
import _ from 'lodash';
import React, {
  ComponentType,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';
import { useHistory } from 'react-router-dom';
import ProductAPI from 'restAPI/ProductAPI';
import UserAPI from 'restAPI/UserAPI';

import { useActiveUserContext } from './ActiveUserContext';

// import { updateURLSearch } from "utils/tableUtils";

enum Actions {
  BU_UPDATED = 'BU_UPDATED',
  UPDATE_FILTERS = 'update_filters',
  PRODUCTS_LOADED = 'products_loaded',
  UPDATE_BU_FILTERS = 'update_bu_filters',
  PRODUCT_DELETE = 'PRODUCT_DELETE',
  DISABLE = 'DISABLE',
}

type UpdateFiltersAction = {
  type: Actions.UPDATE_FILTERS;
  payload: {
    businessUnitId?: number;
    businessUnitName?: string;
    products: number[];
    subProducts: number[];
    roleId?: number;
    appToolCategories: string[];
    groups: string[];
    rememberFilters: boolean;
    history?: History;
  };
};

type Action =
  | UpdateFiltersAction
  | { type: Actions.PRODUCTS_LOADED; payload: number[] }
  | { type: Actions.UPDATE_BU_FILTERS; payload: { filters: [] } }
  | { type: Actions.BU_UPDATED; payload: { id: number; name: string } }
  | { type: Actions.PRODUCT_DELETE; payload: number }
  | { type: Actions.DISABLE; payload: boolean };

type GlobalFilterState = {
  globalFilterProducts: number[];
  globalFilterSubProducts: number[];
  globalFilterBusinessUnit: number;
  globalFilterBusinessUnitName: string;
  globalFilterRoleId: number | undefined;
  globalFilterHash: string;
  globalFilterToolCategories: string[];
  globalFilterGroups: string[];
  globalFilterRemember: boolean;
  globalFilterBusinessUnits?: string[];
  globalFilters?: [];
  disabled?: boolean;
};

type GlobalFilterContextType = GlobalFilterState & {
  updateGlobalFilters: (args: {
    businessUnitId?: number;
    businessUnitName?: string;
    products?: number[];
    subProducts?: number[];
    roleId?: number;
    appToolCategories?: string[];
    groups?: string[];
    rememberFilters?: boolean;
    callback?: () => void;
  }) => () => void;
  onBusinessUnitUpdate: (bu: { id: number; name: string }) => void;
  setInitialProducts: (products: number[]) => void;
  onProductDelete: (productId: number) => void;
  addEventListener: ListenerType;
  removeEventListener: ListenerType;
  buAdded: () => void;
  disable: (disabled: boolean) => void;
};

interface GlobalFilterEvents {
  PRODUCT_ADDED: number[]; // triggerEvent('PRODUCT_ADDED', products)
  BU_ADDED: void; // triggerEvent('BU_ADDED')
}

const GlobalFilterContext = createContext<GlobalFilterContextType>(
  {} as GlobalFilterContextType,
);

type ListenerType = <K extends keyof GlobalFilterEvents>(
  type: K,
  cb: (payload: GlobalFilterEvents[K]) => void,
) => void;

type TriggerEventType = <K extends keyof GlobalFilterEvents>(
  type: K,
  payload: GlobalFilterEvents[K],
) => void;

let GLOBAL_FILTERS: GlobalFilterState;

export const generateGlobalFilterHash = (
  businessUnitId: number,
  products: number[],
  subProducts: number[],
  roleId: number | undefined,
  appToolCategories: string[],
  groups: string[],
) => {
  let hash =
    businessUnitId +
    '##' +
    products.sort((a, b) => a - b).join('#') +
    '##' +
    appToolCategories.sort((a, b) => a.localeCompare(b)).join('#') +
    groups.sort((a, b) => a.localeCompare(b)).join('#');
  if (!_.isEmpty(subProducts)) {
    hash += '##' + subProducts.sort((a, b) => a - b).join('#');
  }
  if (roleId) {
    hash += '##' + roleId;
  }
  return hash;
};

function reducer(state: GlobalFilterState, action: Action) {
  switch (action.type) {
    case Actions.UPDATE_FILTERS: {
      const {
        businessUnitId = state.globalFilterBusinessUnit,
        products,
        subProducts,
        businessUnitName = state.globalFilterBusinessUnitName,
        roleId = undefined,
        // history,
        appToolCategories = state.globalFilterToolCategories,
        groups = state.globalFilterGroups,
        rememberFilters = state.globalFilterRemember,
      } = action.payload;
      const hash = generateGlobalFilterHash(
        businessUnitId,
        products,
        subProducts,
        roleId,
        appToolCategories,
        groups,
      );
      // clear url only if the hash changes
      if (hash !== state.globalFilterHash) {
        // history && updateURLSearch(history, {});
      }
      GLOBAL_FILTERS = {
        ...state,
        globalFilterProducts: products,
        globalFilterSubProducts: subProducts,
        globalFilterBusinessUnit: businessUnitId,
        globalFilterBusinessUnitName: businessUnitName,
        globalFilterRoleId: roleId,
        globalFilterHash: hash,
        globalFilterToolCategories: appToolCategories,
        globalFilterGroups: groups,
        globalFilterRemember: rememberFilters,
      };
      return { ...GLOBAL_FILTERS };
    }
    case Actions.PRODUCTS_LOADED:
      GLOBAL_FILTERS = {
        ...state,
        globalFilterProducts: action.payload,
      };
      return { ...GLOBAL_FILTERS };
    case Actions.UPDATE_BU_FILTERS:
      GLOBAL_FILTERS = {
        ...state,
        globalFilters: [],
        globalFilterBusinessUnits: action.payload.filters,
      };
      return { ...GLOBAL_FILTERS };
    case Actions.BU_UPDATED:
      if (action.payload.id === state.globalFilterBusinessUnit) {
        GLOBAL_FILTERS = {
          ...state,
          globalFilterBusinessUnitName: action.payload.name,
        };
        return { ...GLOBAL_FILTERS };
      }
      return state;
    case Actions.PRODUCT_DELETE: {
      const index = state.globalFilterProducts.indexOf(action.payload);
      if (index !== -1) {
        state.globalFilterProducts.splice(index, 1);
        GLOBAL_FILTERS = {
          ...state,
        };
        return { ...GLOBAL_FILTERS };
      }
      return state;
    }
    case Actions.DISABLE:
      GLOBAL_FILTERS = {
        ...state,
        disabled: action.payload,
      };
      return { ...GLOBAL_FILTERS };
    default:
      return state;
  }
}

export const GlobalFilterContextProvider = ({
  children,
}: {
  children: ReactNode | ReactNode[];
}) => {
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState<
    Partial<{
      [K in keyof GlobalFilterEvents]: ((
        payload: GlobalFilterEvents[K],
      ) => void)[];
    }>
  >({});
  const activeUser = useActiveUserContext();
  const [state, dispatch] = useReducer<
    React.Reducer<GlobalFilterState, Action>
  >(reducer, {
    globalFilterProducts: [],
    globalFilterSubProducts: [],
    globalFilterHash: '',
    globalFilterBusinessUnit: activeUser.businessUnitId,
    globalFilterBusinessUnitName: activeUser.businessUnitsName,
    globalFilterRoleId: activeUser?.roleId,
    globalFilterToolCategories: [],
    globalFilterGroups: [],
    globalFilterRemember: false,
  });

  const {
    globalFilters,
    globalFilterHash,
    globalFilterProducts,
    globalFilterSubProducts,
    globalFilterBusinessUnit,
    globalFilterBusinessUnitName,
    globalFilterRoleId,
    globalFilterToolCategories,
    globalFilterGroups,
    globalFilterRemember,
    disabled,
  } = state;

  const history = useHistory();

  const triggerEvent: TriggerEventType = useCallback(
    (type, payload) => {
      if (events[type]) {
        _.forEach(events[type] ?? [], (entry) => entry(payload));
      }
    },
    [events],
  );

  useEffect(() => {
    ProductAPI.getGlobalFilter()
      .then(
        ({
          appToolCategories,
          productGlobalDtos,
          subProductGlobalDtos,
          groups,
          rememberFilters,
        }) => {
          dispatch({
            type: Actions.UPDATE_FILTERS,
            payload: {
              products: _.filter(productGlobalDtos, (prod) => prod.status).map(
                (prod) => +prod.id,
              ),
              subProducts: _.filter(subProductGlobalDtos, (s) => s.status).map(
                (s) => +s.id,
              ),
              appToolCategories: appToolCategories || [],
              groups: groups || [],
              roleId: activeUser?.roleId,
              rememberFilters,
            },
          });
        },
      )
      .finally(() => setLoading(false));
  }, [history, activeUser?.roleId, globalFilterHash]);

  const updateGlobalFilters = useCallback(
    ({
      businessUnitId,
      businessUnitName,
      products = [],
      subProducts = [],
      roleId = activeUser?.roleId,
      appToolCategories = [],
      groups = [],
      rememberFilters = false,
      callback = _.noop,
    }) => {
      const { promise, cancel } = !_.isUndefined(businessUnitId)
        ? UserAPI.switchBusinessUnit(activeUser.tenantId, businessUnitId, {
            roleId,
          })
        : { promise: Promise.resolve(), cancel: _.noop };

      // Updated filters only when BU is not switched, on BU switch backend handles clearing the filters
      const { promise: promise2, cancel: cancel2 } = _.isUndefined(
        businessUnitId,
      )
        ? ProductAPI.postFilter({
            productId: products,
            subProductId: subProducts,
            appToolCategories,
            groups,
            rememberFilters,
          })
        : { promise: Promise.resolve(), cancel: _.noop };
      Promise.all([promise, promise2]).then(() => {
        dispatch({
          type: Actions.UPDATE_FILTERS,
          payload: {
            history,
            products,
            subProducts,
            businessUnitId,
            businessUnitName,
            roleId,
            appToolCategories,
            groups,
            rememberFilters,
          },
        });
        triggerEvent('PRODUCT_ADDED', products);
        activeUser.refreshAuth();
        callback();
      }, _.noop);
      return () => {
        cancel();
        cancel2();
      };
    },
    [activeUser, triggerEvent, history],
  );

  const buAdded = useCallback(() => {
    triggerEvent('BU_ADDED', undefined);
  }, [triggerEvent]);

  const addEventListener: ListenerType = useCallback((type, cb) => {
    setEvents((events) => ({
      ...events,
      [type]: [...(events[type] || []), cb],
    }));
  }, []);

  const removeEventListener: ListenerType = useCallback((type, cb) => {
    setEvents((events) => ({
      ...events,
      [type]: _.filter(events[type], (callback) => callback !== cb),
    }));
  }, []);

  const onBusinessUnitUpdate = useCallback((businessUnit) => {
    dispatch({
      type: Actions.BU_UPDATED,
      payload: businessUnit,
    });
  }, []);

  const setInitialProducts = useCallback((products) => {
    dispatch({
      type: Actions.PRODUCTS_LOADED,
      payload: products,
    });
  }, []);

  const onProductDelete = useCallback((deletedProductId) => {
    dispatch({
      type: Actions.PRODUCT_DELETE,
      payload: +deletedProductId,
    });
  }, []);

  const disable = useCallback((disabled) => {
    dispatch({
      type: Actions.DISABLE,
      payload: disabled,
    });
  }, []);

  const values = useMemo(
    () => ({
      updateGlobalFilters,
      globalFilters,
      globalFilterHash,
      globalFilterProducts,
      globalFilterSubProducts,
      globalFilterBusinessUnit,
      globalFilterBusinessUnitName,
      globalFilterRoleId,
      globalFilterToolCategories,
      globalFilterGroups,
      globalFilterRemember,
      onBusinessUnitUpdate,
      setInitialProducts,
      onProductDelete,
      addEventListener,
      removeEventListener,
      buAdded,
      disable,
      disabled,
    }),
    [
      updateGlobalFilters,
      globalFilters,
      globalFilterHash,
      globalFilterProducts,
      globalFilterBusinessUnit,
      globalFilterBusinessUnitName,
      globalFilterRoleId,
      globalFilterToolCategories,
      globalFilterGroups,
      globalFilterRemember,
      onBusinessUnitUpdate,
      setInitialProducts,
      onProductDelete,
      addEventListener,
      removeEventListener,
      buAdded,
      disable,
      disabled,
      globalFilterSubProducts,
    ],
  );

  if (loading) {
    return <AppLoader />;
  }

  return (
    <GlobalFilterContext.Provider value={values}>
      {children}
    </GlobalFilterContext.Provider>
  );
};

export const withGlobalFilterContext =
  <T,>(
    Component: ComponentType<T>,
  ): ComponentType<T & { globalFilterContext: GlobalFilterContextType }> =>
  (props: T) => (
    <GlobalFilterContext.Consumer>
      {(context) => <Component globalFilterContext={context} {...props} />}
    </GlobalFilterContext.Consumer>
  );

export function useGlobalFilterContext() {
  return useContext(GlobalFilterContext);
}

export function getGlobalFilter_UNSAFE() {
  return _.cloneDeep(GLOBAL_FILTERS);
}
